import { DEALER_TYPE } from '../../constants/dealer-constants';
import { isComerciales } from '../../helpers/carline-helpers';
import { sanitizeKvps } from '../../helpers/dealer-helpers';
import { isLeadVentaForm, isLeadVentaVehiculoOcasionForm } from '../../helpers/form-helpers';
import { NdpsDealer } from '../../types/dealer-types';
import { arrayToObject } from '../../utils/array-utils';
import { configProviderManager } from '../config-provider';
import { fetchNdpsDomain } from './fetch/ndps-fetch';

declare global {
	interface Window {
		google: any;
	}
}

declare const google: any;

export class DealersService {
	private dealers: NdpsDealer[] = [];
	private token: string;
	private domain: string;
	private type: number[]; // Tipo de dealer (1: vn, 2:ps, 10: vo...)

	constructor(token?: string, domain?: string) {
		this.token = token || (isComerciales() ? 'LCV_DDB_FRM_LEM_REC_LEM' : 'VW_DDB_FRM_LEM_REC_LEM');
		this.domain =
			domain ||
			(isComerciales() ? 'LCV_SF_LEAD_INSTALLATION_VN_VO_SERVICE' : 'VOLKSWAGEN_SF_LEAD_INSTALLATION_VN_VO_SERVICE');

		if (isLeadVentaVehiculoOcasionForm()) {
			this.type = [DEALER_TYPE.VO];
		} else if (isLeadVentaForm()) {
			this.type = [DEALER_TYPE.VN];
		} else {
			this.type = [DEALER_TYPE.VN, DEALER_TYPE.PS, DEALER_TYPE.AG, DEALER_TYPE.VO]; // Para otros formularios, incluir todos los tipos
		}
	}

	// Get dealers usando geoposicion navegador
	async getSortedDealersByCoordinates(lat: number, lon: number): Promise<NdpsDealer[]> {
		await this.fetchDealers();
		const dealersWithDistance = this.dealers
			.map((dealer) => {
				const distance =
					dealer.ShippingLatitude && dealer.ShippingLongitude
						? this.calculateDistance(
								lat,
								lon,
								parseFloat(dealer.ShippingLatitude),
								parseFloat(dealer.ShippingLongitude),
							)
						: Infinity;
				return { ...dealer, Distance: distance };
			})
			.sort((a, b) => (a.Distance || Infinity) - (b.Distance || Infinity));

		return dealersWithDistance;
	}

	async getSortedDealers(input: string): Promise<NdpsDealer[]> {
		await this.fetchDealers();
		if (this.isPostalCode(input)) {
			return this.getPostalCodeDealersFirst(input);
		}
		if (/^\d+$/.test(input)) {
			return this.getPostalCodeDealersFirst(input);
		}
		return this.getLocationDealersFirst(input);
	}

	async fetchDealers(all?: boolean): Promise<void> {
		const response = await fetchNdpsDomain(this.token, this.domain);
		if (response?.status?.code !== 200) {
			throw new Error();
		}
		if (response.content) {
			let dealers = response.content.map((content) => arrayToObject(content.attributes) as NdpsDealer);

			const excludedNames = [
				'VENTAS MODELO AGENCIA',
				'CENT.TECN.SEAT-FLOTAS',
				'CENT.TECN.SEAT-ARRENDAMIENTO',
				'CENT.TCN.SEAT DOT.DIRECTIVOS',
				'CAMBIO DE NOMBRE COMERCIAL',
			];
			dealers = dealers.filter(
				(dealer) => dealer.VGED_CommercialName__c && !excludedNames.includes(dealer.VGED_CommercialName__c),
			);

			// Excluimos dealers sin KVPS
			const excludedKvps = ['ESP-V'];
			dealers = dealers.filter((dealer) => dealer.KVPSCode__c && !excludedKvps.includes(dealer.KVPSCode__c));

			// Filtramos por tipo de dealer según el formulario
			if (!all) {
				dealers = dealers.filter((dealer) => this.type.map(String).includes(dealer.RCY_DealerType__c ?? ''));
			}

			//console.log('🚀 ~ App ~ dealers', domain || this.domain, dealersSummary(dealers));

			this.dealers = dealers;
		}
	}

	async getSuggestions(input: string): Promise<string[]> {
		// Si el input es numérico y tiene 4+ dígitos, combinar Google Maps con códigos de dealers
		if (/^\d{4,}$/.test(input)) {
			await this.fetchDealers();
			const googleSuggestions = await this.getGooglePostalCodeSuggestions(input);
			const dealerSuggestions = this.getDealerPostalCodeSuggestions(input);

			// Combinar y filtrar duplicados, priorizando los validados por Google
			const combined = [...googleSuggestions];
			dealerSuggestions.forEach((code) => {
				if (!combined.includes(code)) {
					combined.push(code);
				}
			});

			return combined.slice(0, 10);
		}

		// Si el input es numérico pero tiene menos de 4 dígitos, no mostrar sugerencias
		if (/^\d+$/.test(input)) {
			return [];
		}

		// Para texto, necesita al menos 3 caracteres
		if (input.length < 3) {
			return [];
		}

		try {
			await this.loadGoogleMapsAPI();
			const service = new google.maps.places.AutocompleteService();

			const result = await new Promise<any[]>((resolve) => {
				service.getPlacePredictions(
					{
						input: input,
						componentRestrictions: { country: 'ES' },
						types: ['(cities)'],
					},
					(predictions: any[] | null, status: string) => {
						if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
							resolve(predictions);
						} else {
							resolve([]);
						}
					},
				);
			});

			return result
				.filter((prediction) => {
					const types = prediction.types || [];
					return (
						types.includes('locality') ||
						types.includes('administrative_area_level_2') ||
						types.includes('administrative_area_level_1')
					);
				})
				.map((prediction) => prediction.description.split(',')[0])
				.slice(0, 5);
		} catch (error) {
			return [];
		}
	}

	private getDealerPostalCodeSuggestions(input: string): string[] {
		return this.dealers
			.map((dealer) => String(dealer.ShippingPostalCode))
			.filter((code) => code && code.startsWith(input))
			.filter((code, index, array) => array.indexOf(code) === index)
			.sort();
	}

	async getCoordinatesByPostalCode(postalCode: string): Promise<{ lat: number; lon: number } | null> {
		return this.getCoorsByPostalCode(postalCode);
	}

	// Ordena dealers priorizando coincidencias exactas de código postal y los demás por distancia
	private async getPostalCodeDealersFirst(postalCode: string): Promise<NdpsDealer[]> {
		// Buscar dealers con código postal exacto
		const exactMatches = this.dealers.filter((dealer) => {
			return String(dealer.ShippingPostalCode) === postalCode;
		});

		exactMatches.forEach((dealer) => {
			dealer.Distance = undefined;
		});

		// Obtener coordenadas del código postal
		const coords = await this.getCoorsByPostalCode(postalCode);

		// Si no hay dealers exactos y no hay coordenadas válidas, no devolver resultados
		if (exactMatches.length === 0 && (!coords || !this.isValidSpanishCoords(coords))) {
			return [];
		}

		// Resto de dealers ordenados por distancia
		const otherDealers = this.getOtherDealers(exactMatches, coords);

		return [...exactMatches, ...otherDealers];
	}

	// Ordena dealers priorizando coincidencias exactas de localidad o ciudad y los demás por distancia
	private async getLocationDealersFirst(location: string): Promise<NdpsDealer[]> {
		const normalizedLocation = this.normalizeText(location);
		let exactMatches = this.dealers.filter((dealer) => {
			if (!dealer.ShippingCity) return false;
			const normalizedCity = this.normalizeText(dealer.ShippingCity);
			return normalizedCity.startsWith(normalizedLocation);
		});

		if (exactMatches.length === 0) {
			exactMatches = this.dealers.filter((dealer) => {
				if (!dealer.Province) return false;
				const normalizedProvince = this.normalizeText(dealer.Province);
				return normalizedProvince.startsWith(normalizedLocation);
			});
		}

		// Ordenar exactMatches por distancia desde las coordenadas de Google Maps por Location
		if (exactMatches.length > 1) {
			const coords = await this.getCoordsByLocation(location);
			if (coords) {
				exactMatches.forEach((dealer) => {
					dealer.Distance =
						dealer.ShippingLatitude && dealer.ShippingLongitude
							? this.calculateDistance(
									coords.lat,
									coords.lon,
									parseFloat(dealer.ShippingLatitude),
									parseFloat(dealer.ShippingLongitude),
								)
							: undefined;
				});
				exactMatches.sort((a, b) => (a.Distance || Infinity) - (b.Distance || Infinity));
			}
		}

		exactMatches.forEach((dealer) => {
			dealer.Distance = undefined;
		});

		const coords = await this.getCoordsByLocation(location);

		if (exactMatches.length === 0 && !coords) {
			return [];
		}

		// Resto de dealers ordenados por distancia y con nodo Distance calculado
		const otherDealers = this.getOtherDealers(exactMatches, coords);

		return [...exactMatches, ...otherDealers];
	}

	private getOtherDealers(exactMatches: NdpsDealer[], coords: { lat: number; lon: number } | null): NdpsDealer[] {
		const otherDealers = coords ? this.dealers.filter((dealer) => !exactMatches.includes(dealer)) : [];

		if (coords && otherDealers) {
			otherDealers.forEach((dealer) => {
				dealer.Distance =
					dealer.ShippingLatitude && dealer.ShippingLongitude
						? this.calculateDistance(
								coords.lat,
								coords.lon,
								parseFloat(dealer.ShippingLatitude),
								parseFloat(dealer.ShippingLongitude),
							)
						: undefined;
			});
			otherDealers.sort((a, b) => (a.Distance || Infinity) - (b.Distance || Infinity));
		}
		return otherDealers;
	}

	private async getGooglePostalCodeSuggestions(input: string): Promise<string[]> {
		try {
			await this.loadGoogleMapsAPI();
			const service = new google.maps.places.AutocompleteService();

			const result = await new Promise<any[]>((resolve) => {
				service.getPlacePredictions(
					{
						input: input,
						componentRestrictions: { country: 'ES' },
						types: ['postal_code'],
					},
					(predictions: any[] | null, status: string) => {
						if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
							resolve(predictions);
						} else {
							resolve([]);
						}
					},
				);
			});

			const postalCodes = result
				.map((prediction) => {
					const description = prediction.description;
					// Extraer código postal de diferentes formatos
					const match = description.match(/\b\d{4,5}\b/);
					return match ? match[0] : null;
				})
				.filter((code): code is string => code !== null)
				.slice(0, 10);

			return postalCodes;
		} catch (error) {
			console.error('Error obteniendo sugerencias de códigos postales.', error);
			return [];
		}
	}

	// Obtiene coordenadas de código postal usando Google Maps
	private async getCoorsByPostalCode(postalCode: string): Promise<{ lat: number; lon: number } | null> {
		return await this.getGoogleCoords(postalCode);
	}

	// Obtiene coordenadas de ubicación usando Google Maps
	private async getCoordsByLocation(location: string): Promise<{ lat: number; lon: number } | null> {
		return await this.getGoogleCoords(location);
	}

	private async loadGoogleMapsAPI(): Promise<void> {
		if (typeof google !== 'undefined' && google.maps) {
			return;
		}

		return new Promise((resolve, reject) => {
			const apiKey = configProviderManager.getGoogleApiKey();
			const script = document.createElement('script');
			script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry,places`;
			script.async = true;
			script.defer = true;
			script.onload = () => resolve();
			script.onerror = () => reject(new Error('Failed to load Google Maps API'));
			document.head.appendChild(script);
		});
	}

	private async getGoogleCoords(address: string): Promise<{ lat: number; lon: number } | null> {
		try {
			await this.loadGoogleMapsAPI();
			const geocoder = new google.maps.Geocoder();
			const result = await new Promise<any[]>((resolve, reject) => {
				geocoder.geocode({ address: `${address}, ES`, region: 'ES' }, (results: any[] | null, status: string) => {
					if (status === 'OK' && results) {
						resolve(results);
					} else {
						reject(status);
					}
				});
			});

			if (result.length > 0) {
				const location = result[0].geometry.location;
				return {
					lat: location.lat(),
					lon: location.lng(),
				};
			}
		} catch (error) {
			// Error en geocodificación
		}
		return null;
	}

	// Comprueba si el texto es un código postal
	private isPostalCode(input: string): boolean {
		return /^\d{4,5}$/.test(input.trim());
	}

	// Verifica si las coordenadas están dentro de España (aproximadamente)
	private isValidSpanishCoords(coords: { lat: number; lon: number }): boolean {
		// Coordenadas aproximadas de España
		const minLat = 35.0; // Sur de España
		const maxLat = 44.0; // Norte de España
		const minLon = -10.0; // Oeste de España
		const maxLon = 5.0; // Este de España

		return coords.lat >= minLat && coords.lat <= maxLat && coords.lon >= minLon && coords.lon <= maxLon;
	}

	// Normaliza texto eliminando espacios, acentos y caracteres especiales para comparaciones
	private normalizeText(text: string): string {
		return text
			.toLowerCase()
			.replace(/\s+/g, '')
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/&#\d+;/g, '')
			.replace(/&[a-z]+;/g, '');
	}

	// Calcula la distancia en kilómetros entre dos puntos usando la fórmula de Haversine
	private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
		const R = 6371; // Radio de la Tierra en kilómetros
		const dLat = ((lat2 - lat1) * Math.PI) / 180;
		const dLon = ((lon2 - lon1) * Math.PI) / 180;
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
		return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	}

	async getDealerByKvps(kvps?: string): Promise<NdpsDealer | undefined> {
		if (!kvps) return undefined;
		await this.fetchDealers(true);

		const dealers = this.dealers.filter((d) => sanitizeKvps(d.KVPSCode__c ?? '') == sanitizeKvps(kvps));

		// Devolver el dealer que coincida con los tipos, o el primero si no hay coincidencias de tipo
		return dealers.find((d) => this.type.map(String).includes(d.RCY_DealerType__c ?? '')) ?? dealers[0] ?? undefined;
	}

	async getConcessionDealers(dealer?: NdpsDealer): Promise<NdpsDealer[] | null> {
		if (!dealer) return null;
		await this.fetchDealers(true);

		const concessionDealers = this.dealers.filter(
			(d) => d.VGED_ConcessionDealerCode__c == dealer.VGED_ConcessionDealerCode__c,
		);

		// Devolver solo los dealers que coincidan con los tipos
		const filtered = concessionDealers.filter((dealer) =>
			this.type.map(String).includes(dealer.RCY_DealerType__c ?? ''),
		);
		return filtered.length > 0 ? filtered : null;
	}

	// Busca dealers por múltiples campos (sin Google ni distancia).
	// Los resultados se ordenan según la prioridad del campo que coincide.
	async searchDealers(input: string): Promise<NdpsDealer[]> {
		await this.fetchDealers();
		const normalizedInput = this.normalizeText(input);
		const rawInput = input.trim().toLowerCase();
		if (!rawInput) return [];

		// Campos ordenados por prioridad: cuanto más arriba, mayor prioridad en el listado.
		const priorityFields: Array<(dealer: NdpsDealer) => boolean> = [
			(d) => this.normalizeText(d.VGED_CommercialName__c ?? '').includes(normalizedInput),
			(d) => (d.KVPSCode__c ?? '').toLowerCase().includes(rawInput),
			(d) => (d.VGED_DealerCode__c ?? '').toLowerCase().includes(rawInput),
			(d) => (d.VGED_ConcessionDealerCode__c ?? '').toLowerCase().includes(rawInput),
			(d) => this.normalizeText(d.ShippingStreet ?? '').includes(normalizedInput),
			(d) => (d.ShippingPostalCode ?? '').toLowerCase().includes(rawInput),
			(d) => this.normalizeText(d.ShippingCity ?? '').includes(normalizedInput),
		];

		return this.dealers
			.map((dealer) => ({
				dealer,
				priority: priorityFields.findIndex((matches) => matches(dealer)),
			}))
			.filter((entry) => entry.priority !== -1)
			.sort((a, b) => a.priority - b.priority)
			.map((entry) => entry.dealer);
	}
}
